import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY, SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY } from "@faire/retailer-visitor-shared/features/Cart/consts";
import { useInvalidateServerCart } from "@faire/retailer-visitor-shared/features/Cart/ServerCart/useInvalidateServerCart";
import { useCartPageSearchQuery } from "@faire/retailer-visitor-shared/features/Cart/useCartPageSearchQuery";
import { useFilterKeys } from "@faire/retailer-visitor-shared/features/Cart/useFilterKeys";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { Storage } from "@faire/web--source/common/Storage";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { QueryParameters as GetCartQueryParams } from "@faire/web-api--source/api/v2/carts/cartToken/get";
import fetchCart from "@faire/web-api--source/api/v2/carts/cartToken/get";
import { useInvalidateQuery as useInvalidateCartQuery, useInvalidateInfiniteQuery as useInvalidateInviniteCartQuery, getInfiniteQueryKey as getCartInfiniteQueryKey } from "@faire/web-api--source/api/v2/carts/cartToken/get-hooks";
import { BrandCartsSortBy } from "@faire/web-api--source/faire/carts/BrandCartsSortBy";
import { ICartV2 } from "@faire/web-api--source/faire/carts/ICartV2";
import { route as cartRoute } from "@faire/web-api--source/routes/www/cart";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import flatMap from "lodash/flatMap";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
var VISIBLE_WINDOW_SIZE = 8;
export var SUBSEQUENT_PAGE_SIZE = 20;
export var INITIAL_PAGE_SIZE = SUBSEQUENT_PAGE_SIZE + VISIBLE_WINDOW_SIZE;
export var useCartQueryParams = function() {
    var filterKeys = useFilterKeys();
    return useCallback(function(param) {
        var savedForLater = param.savedForLater, cursor = param.cursor, query = param.query;
        var max_results = cursor ? SUBSEQUENT_PAGE_SIZE : INITIAL_PAGE_SIZE;
        return GetCartQueryParams.build({
            filter_brand_carts_by_keys: filterKeys !== null && filterKeys !== void 0 ? filterKeys : [],
            saved_for_later: savedForLater,
            brand_tokens: [],
            exclude_alcohol_disclaimer: false,
            sort_brand_carts_by: BrandCartsSortBy.UPDATED_AT,
            cursor: cursor,
            query: query,
            max_results: max_results
        });
    }, [
        filterKeys
    ]);
};
var useCart = function(savedForLater) {
    var _paginatedQueryResult_data;
    var buildQueryParams = useCartQueryParams();
    var query = useCartPageSearchQuery();
    var paginatedQueryResult = useInfiniteQuery({
        queryKey: getCartInfiniteQueryKey(getCartToken(), buildQueryParams({
            savedForLater: savedForLater,
            query: query
        })),
        queryFn: function(param) {
            var pageParam = param.pageParam;
            var params = buildQueryParams({
                savedForLater: savedForLater,
                cursor: pageParam,
                query: query
            });
            return fetchCart(getCartToken(), params);
        },
        initialPageParam: undefined,
        getNextPageParam: function(lastPage) {
            return lastPage.cursor;
        },
        refetchOnMount: false,
        placeholderData: keepPreviousData
    });
    var data = useMemo(function() {
        var _paginatedQueryResult_data, _pages_, _pages_1, _pages_2;
        var pages = (_paginatedQueryResult_data = paginatedQueryResult.data) === null || _paginatedQueryResult_data === void 0 ? void 0 : _paginatedQueryResult_data.pages;
        return ICartV2.build({
            brand_carts: flatMap(pages, function(page) {
                return page.brand_carts;
            }).filter(isNotUndefined),
            filter_sections: flatMap(pages, function(page) {
                return page.filter_sections;
            }).filter(isNotUndefined),
            cart_stats: pages === null || pages === void 0 ? void 0 : (_pages_ = pages[0]) === null || _pages_ === void 0 ? void 0 : _pages_.cart_stats,
            eori_checkout_content: pages === null || pages === void 0 ? void 0 : (_pages_1 = pages[0]) === null || _pages_1 === void 0 ? void 0 : _pages_1.eori_checkout_content,
            cursor: pages === null || pages === void 0 ? void 0 : (_pages_2 = pages[pages.length - 1]) === null || _pages_2 === void 0 ? void 0 : _pages_2.cursor
        });
    }, [
        (_paginatedQueryResult_data = paginatedQueryResult.data) === null || _paginatedQueryResult_data === void 0 ? void 0 : _paginatedQueryResult_data.pages
    ]);
    return _object_spread_props(_object_spread({}, paginatedQueryResult), {
        data: data
    });
};
export var useActiveCart = function() {
    return useCart(false);
};
export var useSavedCart = function() {
    return useCart(true);
};
export var useInvalidateCart = function() {
    var location = useLocation();
    var buildQueryParams = useCartQueryParams();
    var invalidateCartQuery = useInvalidateCartQuery();
    var invalidateInfiniteCartQuery = useInvalidateInviniteCartQuery();
    var searchQuery = useCartPageSearchQuery();
    var query = useMemo(function() {
        return location.pathname === cartRoute ? searchQuery : undefined;
    }, [
        location.pathname,
        searchQuery
    ]);
    var invalidateCartRequest = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(param) {
            var savedForLater, query, args;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        savedForLater = param.savedForLater, query = param.query;
                        args = [
                            getCartToken(),
                            buildQueryParams({
                                savedForLater: savedForLater,
                                query: query
                            }),
                            undefined,
                            {
                                refetchType: "all"
                            }
                        ];
                        return [
                            4,
                            Promise.all([
                                invalidateInfiniteCartQuery.apply(void 0, _to_consumable_array(args)),
                                invalidateCartQuery.apply(void 0, _to_consumable_array(args))
                            ])
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2
                        ];
                }
            });
        });
        return function(_) {
            return _ref.apply(this, arguments);
        };
    }(), [
        buildQueryParams,
        invalidateCartQuery,
        invalidateInfiniteCartQuery
    ]);
    var _useInvalidateServerCart = useInvalidateServerCart(), invalidateActiveServerCart = _useInvalidateServerCart.invalidateActiveServerCart, invalidateSavedServerCart = _useInvalidateServerCart.invalidateSavedServerCart;
    var invalidateCart = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(savedForLater) {
            var promises, queries, key;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        promises = [];
                        if (savedForLater) {
                            promises.push(invalidateSavedServerCart());
                        } else {
                            promises.push(invalidateActiveServerCart());
                        }
                        queries = getQueriesFromStorage(savedForLater);
                        if (queries.length > 0) {
                            queries.forEach(function(q) {
                                promises.push(invalidateCartRequest({
                                    savedForLater: savedForLater,
                                    query: q
                                }));
                            });
                            key = savedForLater ? SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY : ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY;
                            // All search queries have been dealt with
                            Storage.session.removeItem(key);
                        } else {
                            promises.push(invalidateCartRequest({
                                savedForLater: savedForLater,
                                query: query
                            }));
                        }
                        return [
                            4,
                            Promise.all(promises)
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2
                        ];
                }
            });
        });
        return function(savedForLater) {
            return _ref.apply(this, arguments);
        };
    }(), [
        invalidateActiveServerCart,
        invalidateCartRequest,
        invalidateSavedServerCart,
        query
    ]);
    var invalidateActiveCart = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        invalidateCart(false)
                    ];
                case 1:
                    _state.sent();
                    return [
                        2
                    ];
            }
        });
    }), [
        invalidateCart
    ]);
    var invalidateSavedCart = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        invalidateCart(true)
                    ];
                case 1:
                    _state.sent();
                    return [
                        2
                    ];
            }
        });
    }), [
        invalidateCart
    ]);
    var invalidateEntireCart = useCallback(function() {
        return Promise.all([
            invalidateActiveCart(),
            invalidateSavedCart()
        ]);
    }, [
        invalidateActiveCart,
        invalidateSavedCart
    ]);
    return {
        invalidateActiveCart: invalidateActiveCart,
        invalidateSavedCart: invalidateSavedCart,
        invalidateEntireCart: invalidateEntireCart
    };
};
var getQueriesFromStorage = function(savedForLater) {
    var key = savedForLater ? SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY : ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY;
    var queries = Storage.session.getItem(key);
    return queries ? JSON.parse(queries) : [];
};
export var usePrefetchCart = function() {
    var queryClient = useQueryClient();
    var buildQueryParams = useCartQueryParams();
    var prefetchCart = useCallback(function(savedForLater) {
        var cartToken = getCartToken();
        var queryParams = buildQueryParams({
            savedForLater: savedForLater
        });
        var queryFn = function() {
            return fetchCart(cartToken, queryParams);
        };
        /**
         * We don't have a `usePrefetchInfiniteQuery` hook yet, so we need to
         * use the `prefetchQuery` method directly from the queryClient.
         */ return queryClient.prefetchInfiniteQuery({
            queryKey: getCartInfiniteQueryKey(cartToken, queryParams),
            queryFn: queryFn,
            initialPageParam: undefined
        });
    }, [
        buildQueryParams,
        queryClient
    ]);
    var prefetchActiveCart = useCallback(function() {
        return prefetchCart(false);
    }, [
        prefetchCart
    ]);
    var prefetchSavedCart = useCallback(function() {
        return prefetchCart(true);
    }, [
        prefetchCart
    ]);
    return {
        prefetchActiveCart: prefetchActiveCart,
        prefetchSavedCart: prefetchSavedCart
    };
};
